'use client';

import NeedRecommend from '@/app/[locale]/(home)/_components/Header/_components/CartPopover/NeedRecommend';
import CardRow from '@/app/[locale]/(universal)/brand/_components/Single/components/cart/_components/CardRow';
import useAutoAddingProduct from '@/_hooks/useAutoAddingProduct';
import { useCustomLocale } from '@/_hooks/useCustomLocale';
import productService from '@/_service/product.service';
import { IRedux } from '@/_store/types';
import { IProduct } from '@/entities/product/model/types';
import { getPlatform } from '@/_utils/platform';
import {
  Center,
  Spinner,
  Stack,
  StackSeparator,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import useDiscount from '../_hooks/useDiscount';

interface ProductsProps {
  selectedShipperId: string;
  totalPrice: number;
}

export default function Products({ totalPrice, selectedShipperId }: ProductsProps) {
  const { user, cartStore, branches, session } = useSelector(
    (state: IRedux) => ({
      user: state.auth.user,
      cartStore: state.cart.cart,
      branches: state.common.branches,
      session: state.session,
    }),
    shallowEqual,
  );
  const { is_hall } = session;

  const currentLocale = useCustomLocale();
  const shipperId = selectedShipperId;
  const branch = branches?.[shipperId];
  const cart = cartStore?.[shipperId]?.products || [];

  const { productIds, recommendProducts } = useAutoAddingProduct({ cart, selectedShipperId });

  const { data: productData, isLoading } = useQuery({
    queryKey: ['product-all-cart', productIds, shipperId],
    queryFn: () =>
      productService
        .getAll({
          page: 1,
          limit: 50,
          fields: 'modifiers,categories,weight,measurement',
          client_id: user?.id || '',
          menu_id: branch?.menu_id,
          product_ids: productIds,
        }, shipperId)
        .then((res) => res?.data),
    enabled: cart?.length > 0 && !!shipperId,
    select: (res) => {
      const transformed = res?.products?.reduce(
        (acc: Record<string, IProduct>, item) => {
          acc[item.id] = item;
          return acc;
        },
        {},
      );

      return transformed || {};
    },
  });

  const source = useMemo(() => {
    const result = getPlatform({ is_hall });

    return result;
  }, [is_hall]);

  const { discounts } = useDiscount(shipperId, source, totalPrice);

  // useEffect(() => {
  //   if (productData && !Object.keys(productData)?.length && shipperId) {
  //     dispatch(CLEAR({ shipper_id: shipperId }));
  //   }
  // }, [productData, shipperId, dispatch]);

  if (isLoading) {
    return (
      <Center h="200px">
        <Spinner size="lg" color="primary" />
      </Center>
    );
  }

  if (!cart?.length || !productData) {
    return null;
  }

  return (
    <>
      <Stack
        mt={4}
        separator={<StackSeparator borderColor="background" />}
        gap={3}
        px={4}
      >
        {cart?.map(
          (item, idx) => (
            <CardRow
              shipperId={shipperId}
              key={item.key + idx}
              cartItem={item}
              data={productData?.[item?.product_id]}
            />
          ),
        )}
        {discounts?.map(
          (el, idx) =>
            el?.discount_mode === 'bonus_product' && el?.bonus_product?.id && (
              <CardRow
                shipperId={shipperId}
                index={idx + 1}
                cartItem={{
                  image: el?.bonus_product?.image || '',
                  key: '',
                  price: 0,
                  comment: '',
                  variants: {},
                  type: 'simple',
                  product_name: '',
                  category_name: '',
                  discount_percent: 0,
                  order_modifiers: [],
                  price_with_discount: 0,
                  in_stop: false,
                  default_product: false,
                  product_id: '',
                  quantity: el?.bonus_product?.count || 1,
                  addition_type: 2,

                  shipper_id: shipperId,
                  source_id: source,
                  branch_id: branch?.id || '',
                  menu_id: branch?.menu_id || '',
                }}
                bonus_product={{
                  is_bonus: true,
                  product: el?.bonus_product,
                }}
                price={el?.bonus_product?.out_price}
                key={el?.discount_id}
                title={el?.bonus_product?.title?.[currentLocale]}
                readOnly={true}
              />
            ),
        )}
      </Stack>
      {Boolean(recommendProducts && recommendProducts?.length > 0) && (
        <NeedRecommend
          products={recommendProducts}
          productData={productData}
          shipperId={shipperId}
          menuId={branch?.menu_id || ''}
          branchId={branch?.id || ''}
          sourceId={source}
        />
      )}
    </>
  );
}
